import { getQuestionListApi } from "@/api/services/admin/learning-content";
import { TGetQuestionListItemDto } from "@/api/services/admin/learning-content/dtos/get-question-list.dto";
import { DATE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { SUBJECT_TABLE_PER_PAGE } from "@/features/learning-content/constants/pagination";
import { TQuestionData } from "@/features/learning-content/types/learningContent";
import { mapExplanationsToLanguages } from "@/features/learning-content/utils/map-explanations-to-languages";
import { dayjs } from "@/utils/dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

// Count non-null incorrect answers
const countIncorrectAnswers = (item: TGetQuestionListItemDto): number => {
  return [item.incorrectAnswer1, item.incorrectAnswer2, item.incorrectAnswer3, item.incorrectAnswer4, item.incorrectAnswer5].filter((a) => a != null)
    .length;
};

// Convert API question item to frontend TQuestionData format
const mapQuestionItem = (item: TGetQuestionListItemDto): TQuestionData => ({
  id: Number(item.id),
  wordId: item.sheetRowId != null ? String(item.sheetRowId) : "",
  difficulty: item.difficulty ?? "",
  questionText: item.questionText ?? "",
  choices: 1 + countIncorrectAnswers(item),
  correctChoices: 1,
  incorrectChoiceCount: countIncorrectAnswers(item),
  hasImage: !!item.imageFile,
  imageUrl: item.imageFile ?? undefined,
  correctAnswerLabel: item.correctAnswer ?? "",
  registeredDate: item.lastModifiedDate ? dayjs(item.lastModifiedDate).format(DATE_FORMAT) : "",
  supportedLanguages: mapExplanationsToLanguages(item.explanations),
});

/**
 * Custom hook for fetching paginated question list by subject ID
 */
export const useQuestionList = (subjectId: string | undefined) => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_QUESTION_LIST, subjectId, page],
    queryFn: () => getQuestionListApi(subjectId!, { page, perPage: SUBJECT_TABLE_PER_PAGE }),
    enabled: !!subjectId,
  });

  const response = query.data;
  const pagination = response?.pagination;
  const items = response?.data ?? [];

  const data = useMemo(() => {
    return items.map(mapQuestionItem);
  }, [items]);

  // Invalidate all pages after sync/import
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_QUESTION_LIST, subjectId] });
  };

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    page,
    totalPage: pagination?.totalPage ?? 1,
    totalCount: pagination?.total ?? items.length,
    onPageChange: setPage,
    refetch,
  };
};
